import { useCallback, useEffect, useRef, useState } from 'react';
import { debounce } from 'underscore';
import Icon from '@ui/icons/icon';
import Text from '@ui/text/text/text';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';

interface NumberFieldProps {
  label?: string;
  ariaLabel?: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  allowNegative?: boolean;
  min?: number;
  max?: number;
  icon?: string;
  disabled?: boolean;
  // holds onChange until typing pauses, for an update too expensive to run on every keystroke - the
  // field still shows each keystroke, and whatever is waiting lands on blur
  debounceTime?: number;
}

export default function NumberField({
  label,
  ariaLabel,
  value,
  onChange,
  allowNegative = true,
  min,
  max,
  icon,
  disabled,
  debounceTime,
}: NumberFieldProps) {
  const [displayValue, setDisplayValue] = useState(value?.toString() ?? '');
  const isFocused = useRef(false);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  const commitLater = useRef(
    debounce((next: number | undefined) => onChangeRef.current(next), debounceTime ?? 0)
  ).current;
  const commit = useCallback(
    (next: number | undefined) => {
      if (debounceTime) commitLater(next);
      else onChange(next);
    },
    [commitLater, debounceTime, onChange]
  );

  useEffect(() => {
    if (!isFocused.current) setDisplayValue(value?.toString() ?? '');
  }, [value]);

  const clamp = useCallback(
    (n: number) => {
      let clamped = n;
      if (min !== undefined) clamped = Math.max(min, clamped);
      if (max !== undefined) clamped = Math.min(max, clamped);
      return clamped;
    },
    [min, max]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (newValue === '' || newValue === '-') {
        setDisplayValue(newValue);
        if (newValue === '') commit(undefined);
        return;
      }
      const regex = allowNegative ? /^-?\d*$/ : /^\d*$/;
      if (!regex.test(newValue)) return;
      const clamped = clamp(Number(newValue));
      setDisplayValue(String(clamped));
      commit(clamped);
    },
    [allowNegative, clamp, commit]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      e.preventDefault();
      const step = e.shiftKey ? 10 : 1;
      const current = Number(displayValue) || 0;
      const next = clamp(e.key === 'ArrowUp' ? current + step : current - step);
      if (!allowNegative && next < 0) return;
      setDisplayValue(String(next));
      commit(next);
    },
    [allowNegative, clamp, displayValue, commit]
  );

  return (
    <InputWithOverlay
      jsxLabel={
        label
          ? () => (
              <Text className="L2 padding-l-16 margin-b-2" design="GlobalIcoText3">
                {label}
              </Text>
            )
          : undefined
      }
      inputHeight="32px"
      innerContentClassName="padding-l-12"
      renderInnerContent={icon ? () => <Icon name={icon} size={16} color="var(--global-ico-text-1)" /> : undefined}
      inputProps={{
        type: 'text',
        inputMode: 'numeric',
        value: displayValue,
        onChange: handleChange,
        onKeyDown: handleKeyDown,
        onFocus: () => {
          isFocused.current = true;
        },
        onBlur: () => {
          isFocused.current = false;
          if (!debounceTime) return;
          // lands whatever is still waiting, so the field can't snap back to the older value on blur
          commitLater.cancel();
          const typed = displayValue === '' ? undefined : Number(displayValue);
          if (displayValue !== '-' && typed !== value) onChange(typed);
        },
        disabled,
        'aria-label': label ? undefined : ariaLabel,
        className: icon ? 'padding-l-32' : undefined,
      }}
    />
  );
}
